import { useQuery, useMutation, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { useCallback, useState } from 'react'

import {
  listInvoicePayments,
  recordManualPayment,
  processStripeRefund,
  voidManualPayment,
  voidInvoice,
  markInvoiceAsPaid,
  sendPaymentReminder,
  getInvoice,
  getInvoiceLitesByIds,
  listInvoices,
  createPaymentSchedule,
  updatePaymentSchedule,
  deletePaymentSchedule,
  replacePaymentSchedules,
  getDashboardSummary,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListInvoicePaymentsRequestSchema,
  ExportInvoicePaymentsRequestSchema,
  VoidInvoiceRequestSchema,
} from '@/gen/cortado/v1/invoices_pb'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { GetInvoiceResponseSchema } from '@/gen/cortado/v1/cortado_pb'
import { ManualPaymentMethodEnum, PaymentScheduleStatusEnum, StripePaymentStatusEnum } from '@/gen/cortado/v1/settings_pb'
import type { ManualPayment, PaymentSchedule, StripePayment } from '@/gen/cortado/v1/invoices_pb'
import { authStore } from './auth.store'
import { invalidateCrossEntityEmailCaches } from '@/stores/email-thread-cache'

/**
 * Hook to fetch all payments (Stripe + manual) for an invoice
 *
 * @param invoiceId - The ID of the invoice to fetch payments for
 * @returns Query result with stripe payments, manual payments, and loading state
 */
export function useInvoicePayments(invoiceId: string) {
  const session = useStore(authStore, (state) => state.session)

  const query = useQuery(
    listInvoicePayments,
    create(ListInvoicePaymentsRequestSchema, { invoiceId }),
    {
      enabled: !!session && !!invoiceId,
      staleTime: 30 * 1000, // 30 seconds - payments can change frequently
    }
  )

  return {
    stripePayments: query.data?.stripePayments || [],
    manualPayments: query.data?.manualPayments || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    hasLoaded: query.isSuccess || !!query.data,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Filter options for payment queries
 */
export interface PaymentFilterOptions {
  dateFrom?: Date
  dateTo?: Date
  amountMinCents?: number
  amountMaxCents?: number
  stripeOnly?: boolean
  manualOnly?: boolean
  manualMethods?: ManualPaymentMethodEnum[]
  referenceSearch?: string
  includeVoided?: boolean
}

/**
 * Hook to fetch payments with optional filtering
 *
 * @param invoiceId - The ID of the invoice to fetch payments for
 * @param filters - Optional filter parameters
 * @returns Query result with stripe payments, manual payments, and loading state
 */
export function useInvoicePaymentsFiltered(invoiceId: string, filters?: PaymentFilterOptions) {
  const session = useStore(authStore, (state) => state.session)

  const query = useQuery(
    listInvoicePayments,
    create(ListInvoicePaymentsRequestSchema, {
      invoiceId,
      dateFrom: filters?.dateFrom ? timestampFromDate(filters.dateFrom) : undefined,
      dateTo: filters?.dateTo ? timestampFromDate(filters.dateTo) : undefined,
      amountMinCents: filters?.amountMinCents ? BigInt(filters.amountMinCents) : undefined,
      amountMaxCents: filters?.amountMaxCents ? BigInt(filters.amountMaxCents) : undefined,
      stripeOnly: filters?.stripeOnly,
      manualOnly: filters?.manualOnly,
      manualMethods: filters?.manualMethods,
      referenceSearch: filters?.referenceSearch,
      includeVoided: filters?.includeVoided,
    }),
    {
      enabled: !!session && !!invoiceId,
      staleTime: 30 * 1000, // 30 seconds - payments can change frequently
    }
  )

  return {
    stripePayments: query.data?.stripePayments || [],
    manualPayments: query.data?.manualPayments || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    hasLoaded: query.isSuccess || !!query.data,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to record a manual payment (check, wire transfer, cash, ACH)
 *
 * @param invoiceId - The ID of the invoice to record payment for
 * @returns Mutation function and state
 */
export function useRecordManualPayment(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(recordManualPayment, {
    onSuccess: (response) => {
      // Update invoice cache with the returned invoice
      if (response.invoice) {
        const invoiceQueryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: invoiceId },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          invoiceQueryKey,
          create(GetInvoiceResponseSchema, { invoice: response.invoice })
        )
      }

      // Invalidate payments list to show new payment
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoicePayments,
          transport,
          input: { invoiceId },
          cardinality: 'finite',
        }),
      })
    },
    onSettled: () => {
      // Invalidate the dashboard aggregate so its payment schedule cards refresh
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    recordPayment: mutation.mutateAsync,
    isRecording: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * Hook to process a Stripe refund
 *
 * @param invoiceId - The ID of the invoice the payment belongs to
 * @returns Mutation function and state
 */
export function useProcessStripeRefund(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(processStripeRefund, {
    onSuccess: (response) => {
      // Update invoice cache with the returned invoice
      if (response.invoice) {
        const invoiceQueryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: invoiceId },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          invoiceQueryKey,
          create(GetInvoiceResponseSchema, { invoice: response.invoice })
        )
      }

      // Invalidate payments list to show updated payment
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoicePayments,
          transport,
          input: { invoiceId },
          cardinality: 'finite',
        }),
      })
    },
    onSettled: () => {
      // Invalidate the dashboard aggregate so its payment schedule cards refresh
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    processRefund: mutation.mutateAsync,
    isProcessing: mutation.isPending,
    error: mutation.error,
    stripeRefundId: mutation.data?.stripeRefundId,
  }
}

/**
 * Hook to void a manual payment
 *
 * @param invoiceId - The ID of the invoice the payment belongs to
 * @returns Mutation function and state
 */
export function useVoidManualPayment(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(voidManualPayment, {
    onSuccess: (response) => {
      // Update invoice cache with the returned invoice
      if (response.invoice) {
        const invoiceQueryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: invoiceId },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          invoiceQueryKey,
          create(GetInvoiceResponseSchema, { invoice: response.invoice })
        )
      }

      // Invalidate payments list to show voided payment
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoicePayments,
          transport,
          input: { invoiceId },
          cardinality: 'finite',
        }),
      })
    },
    onSettled: () => {
      // Invalidate the dashboard aggregate so its payment schedule cards refresh
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    voidPayment: mutation.mutateAsync,
    isVoiding: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * Hook to void an invoice (cancel it and release payment schedules)
 *
 * @param invoiceId - The ID of the invoice to void
 * @returns Mutation function and state
 */
export function useVoidInvoice(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(voidInvoice, {
    onSuccess: (response) => {
      // Update invoice cache with the returned invoice
      if (response.invoice) {
        const invoiceQueryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: invoiceId },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          invoiceQueryKey,
          create(GetInvoiceResponseSchema, { invoice: response.invoice })
        )
      }

      // Invalidate invoice list to reflect void status
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoices,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    voidInvoice: mutation.mutateAsync,
    isVoiding: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * Hook to void a batch of invoices sequentially (e.g. when cancelling an
 * event and the user opts to void the booking's active invoices).
 *
 * Unlike {@link useVoidInvoice}, this isn't parameterized by a single invoice
 * ID — callers pass the full list of `{ id, force }` pairs to void per call,
 * so a single hook instance can void an arbitrary set without violating the
 * rules of hooks. Failures for individual invoices are collected and
 * returned rather than thrown, so a partial failure doesn't stop the rest of
 * the batch.
 *
 * @returns `voidInvoices(items)` resolving to the list of invoice IDs that
 *   failed to void, and an `isVoiding` pending flag.
 */
export function useVoidInvoicesBatch() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const mutation = useMutation(voidInvoice)

  const voidInvoices = async (
    items: Array<{ id: string; force: boolean }>
  ): Promise<string[]> => {
    const failedInvoiceIds: string[] = []

    for (const item of items) {
      try {
        const response = await mutation.mutateAsync(
          create(VoidInvoiceRequestSchema, { invoiceId: item.id, force: item.force })
        )
        if (response.invoice) {
          const invoiceQueryKey = createConnectQueryKey({
            schema: getInvoice,
            transport,
            input: { id: item.id },
            cardinality: 'finite',
          })
          queryClient.setQueryData(
            invoiceQueryKey,
            create(GetInvoiceResponseSchema, { invoice: response.invoice })
          )
        }
      } catch {
        failedInvoiceIds.push(item.id)
      }
    }

    // Invalidate list caches so booking invoice cards (which read via
    // getInvoiceLitesByIds) and invoice list pages reflect the voids.
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: listInvoices, transport, cardinality: undefined }),
    })
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: getInvoiceLitesByIds, transport, cardinality: undefined }),
    })

    return failedInvoiceIds
  }

  return {
    voidInvoices,
    isVoiding: mutation.isPending,
  }
}

/**
 * Hook to mark an invoice as paid by recording a manual payment for the full balance
 *
 * @param invoiceId - The ID of the invoice to mark as paid
 * @returns Mutation function and state
 */
export function useMarkInvoiceAsPaid(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(markInvoiceAsPaid, {
    onSuccess: (response) => {
      // Update invoice cache with the returned invoice
      if (response.invoice) {
        const invoiceQueryKey = createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: invoiceId },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          invoiceQueryKey,
          create(GetInvoiceResponseSchema, { invoice: response.invoice })
        )
      }

      // Invalidate payments list to show new payment
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoicePayments,
          transport,
          input: { invoiceId },
          cardinality: 'finite',
        }),
      })

      // Invalidate invoice list to reflect paid status
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoices,
          transport,
          cardinality: undefined,
        }),
      })
    },
    onSettled: () => {
      // Invalidate the dashboard aggregate so its payment schedule cards refresh
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getDashboardSummary,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    markAsPaid: mutation.mutateAsync,
    isMarking: mutation.isPending,
    error: mutation.error,
    payment: mutation.data?.payment,
  }
}

/**
 * Hook to send a payment reminder email to the customer
 *
 * @returns Mutation function and state
 */
export function useSendPaymentReminder() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(sendPaymentReminder, {
    onSuccess: () => {
      // Refresh the record's Conversations (and legacy Emails) tab now that
      // reminders are persisted.
      invalidateCrossEntityEmailCaches(queryClient, transport)
    },
  })

  return {
    sendReminder: mutation.mutateAsync,
    isSending: mutation.isPending,
    error: mutation.error,
    success: mutation.data?.success,
    message: mutation.data?.message,
  }
}

/**
 * Hook to create a payment schedule for an existing invoice
 *
 * @param invoiceId - The ID of the invoice the schedule belongs to
 * @returns Mutation function and state
 */
export function useCreatePaymentSchedule(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createPaymentSchedule, {
    onSuccess: (response) => {
      if (response.paymentSchedule) {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getInvoice,
            transport,
            input: { id: invoiceId },
            cardinality: 'finite',
          }),
        })
      }

      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoices,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    createSchedule: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * Hook to update a payment schedule
 *
 * @param invoiceId - The ID of the invoice the schedule belongs to
 * @returns Mutation function and state
 */
export function useUpdatePaymentSchedule(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updatePaymentSchedule, {
    onSuccess: (response) => {
      // Update invoice cache to reflect schedule changes
      if (response.paymentSchedule) {
        // Invalidate the invoice query to refetch with updated schedules
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getInvoice,
            transport,
            input: { id: invoiceId },
            cardinality: 'finite',
          }),
        })
      }

      // Invalidate invoice list
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoices,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    updateSchedule: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
    updatedSchedule: mutation.data?.paymentSchedule,
  }
}

/**
 * Hook to atomically replace the full set of payment schedules for an invoice.
 * Deletes any schedules not in the provided set and inserts the rest in a single transaction.
 *
 * @param invoiceId - The ID of the invoice whose schedules should be replaced
 * @returns Mutation function and state
 */
export function useReplacePaymentSchedules(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(replacePaymentSchedules, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: invoiceId },
          cardinality: 'finite',
        }),
      })

      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoices,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    replaceSchedules: mutation.mutateAsync,
    isReplacing: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * Hook to delete a payment schedule
 *
 * @deprecated Use `useReplacePaymentSchedules` instead. Retained for one release to
 * avoid breaking in-flight consumers; remove after downstream call sites migrate.
 * @param invoiceId - The ID of the invoice the schedule belongs to
 * @returns Mutation function and state
 */
export function useDeletePaymentSchedule(invoiceId: string) {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(deletePaymentSchedule, {
    onSuccess: () => {
      // Invalidate the invoice query to refetch with updated schedules
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getInvoice,
          transport,
          input: { id: invoiceId },
          cardinality: 'finite',
        }),
      })

      // Invalidate invoice list
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listInvoices,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    deleteSchedule: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error,
  }
}

// ==================== Helper Functions ====================

/**
 * Format manual payment method enum to display string
 */
export function formatPaymentMethod(method: ManualPaymentMethodEnum): string {
  switch (method) {
    case ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CHECK:
      return 'Check'
    case ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_WIRE_TRANSFER:
      return 'Wire Transfer'
    case ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CASH:
      return 'Cash'
    case ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_ACH:
      return 'ACH'
    default:
      return 'Unknown'
  }
}

/**
 * Format Stripe payment method type to display string
 */
export function formatStripePaymentMethod(payment: StripePayment): string {
  const method = payment.paymentMethodType
  const brand = payment.paymentMethodBrand
  const last4 = payment.paymentMethodLast4
  const walletType = payment.paymentMethodWalletType

  if (walletType) {
    // Format wallet types
    switch (walletType) {
      case 'apple_pay':
        return 'Apple Pay'
      case 'google_pay':
        return 'Google Pay'
      case 'link':
        return 'Link'
      default:
        return walletType
    }
  }

  if (method === 'card' && brand && last4) {
    const brandName = brand.charAt(0).toUpperCase() + brand.slice(1)
    return `${brandName} •••• ${last4}`
  }

  if (method) {
    return method.charAt(0).toUpperCase() + method.slice(1).replace('_', ' ')
  }

  return 'Card'
}

/**
 * Get payment status with label and variant for badges
 */
export function getPaymentStatus(payment: StripePayment): {
  label: string
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
} {
  if (payment.refunded) {
    if (payment.amountRefundedCents >= payment.amountCents) {
      return { label: 'Fully Refunded', variant: 'destructive' }
    }
    return { label: 'Partially Refunded', variant: 'secondary' }
  }

  switch (payment.status) {
    case StripePaymentStatusEnum.STRIPE_PAYMENT_STATUS_SUCCEEDED:
      return { label: 'Succeeded', variant: 'default' }
    case StripePaymentStatusEnum.STRIPE_PAYMENT_STATUS_PENDING:
      return { label: 'Pending', variant: 'outline' }
    case StripePaymentStatusEnum.STRIPE_PAYMENT_STATUS_FAILED:
      return { label: 'Failed', variant: 'destructive' }
    default:
      return { label: 'Unknown', variant: 'outline' }
  }
}

/**
 * Get manual payment status with label and variant
 */
export function getManualPaymentStatus(payment: ManualPayment): {
  label: string
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
} {
  if (payment.voided) {
    return { label: 'Voided', variant: 'destructive' }
  }
  return { label: 'Recorded', variant: 'default' }
}

/**
 * Calculate the refundable amount for a Stripe payment
 */
export function getRefundableAmount(payment: StripePayment): bigint {
  return BigInt(payment.amountCents) - BigInt(payment.amountRefundedCents)
}

/**
 * Check if a Stripe payment can be refunded
 */
export function canRefundPayment(payment: StripePayment): boolean {
  // Can refund if payment succeeded and has remaining refundable amount
  return (
    payment.status === StripePaymentStatusEnum.STRIPE_PAYMENT_STATUS_SUCCEEDED &&
    getRefundableAmount(payment) > BigInt(0)
  )
}

/**
 * Check if a manual payment can be voided
 */
export function canVoidPayment(payment: ManualPayment): boolean {
  return !payment.voided
}

/**
 * Combine and sort all payments by date (newest first)
 */
export function combineAndSortPayments(
  stripePayments: StripePayment[],
  manualPayments: ManualPayment[]
): Array<{ type: 'stripe' | 'manual'; payment: StripePayment | ManualPayment; date: Date }> {
  const combined: Array<{ type: 'stripe' | 'manual'; payment: StripePayment | ManualPayment; date: Date }> = []

  // Add Stripe payments
  stripePayments.forEach((payment) => {
    const date = payment.stripeCreatedAt
      ? new Date(Number(payment.stripeCreatedAt.seconds) * 1000)
      : payment.createdAt
        ? new Date(Number(payment.createdAt.seconds) * 1000)
        : new Date()
    combined.push({ type: 'stripe', payment, date })
  })

  // Add manual payments
  manualPayments.forEach((payment) => {
    const date = payment.paymentDate
      ? new Date(Number(payment.paymentDate.seconds) * 1000)
      : payment.createdAt
        ? new Date(Number(payment.createdAt.seconds) * 1000)
        : new Date()
    combined.push({ type: 'manual', payment, date })
  })

  // Sort by date (newest first)
  combined.sort((a, b) => b.date.getTime() - a.date.getTime())

  return combined
}

/**
 * Payment schedules whose checkout session completed but the payment hasn't
 * settled yet (e.g. ACH, which clears 3-5 business days later). `ListInvoicePayments`
 * only returns settled `stripe_payments`/`manual_payments` rows, so these are
 * derived from the invoice's own payment schedules instead of a new query.
 */
export function getInFlightPaymentSchedules(schedules: PaymentSchedule[] = []): PaymentSchedule[] {
  return schedules.filter(
    (schedule) => schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED
  )
}

/**
 * Outstanding amount for an in-flight schedule, including the outstanding tip.
 * `activeCheckoutSession` is never populated once a schedule reaches
 * PAYMENT_INITIATED (the session has already moved to "Complete" by then), so
 * the schedule's own fields are the only reliable source. The tip is computed
 * upstream from the schedule's tip fields and folded into the actual Stripe
 * charge (`backend/pkg/services/invoice/checkout_session.go`), so it must be
 * included here — this amount represents money that moved, unlike the
 * tip-exclusive base shown on the payment schedule itself.
 */
export function getInFlightPaymentAmountCents(schedule: PaymentSchedule): bigint {
  const remainingBase = schedule.amountCents - schedule.amountPaidCents
  const remainingTip = schedule.expectedTipCents - schedule.tipPaidCents
  const base = remainingBase > 0n ? remainingBase : 0n
  const tip = remainingTip > 0n ? remainingTip : 0n
  return base + tip
}

/**
 * When the schedule was last moved to PAYMENT_INITIATED — the backend touches
 * `updated_at` on that transition, so it's the only real timestamp available.
 */
export function getInFlightPaymentInitiatedAt(schedule: PaymentSchedule): Date | undefined {
  return schedule.updatedAt ? new Date(Number(schedule.updatedAt.seconds) * 1000) : undefined
}

/**
 * Get payment method options for the record payment form
 */
export function getPaymentMethodOptions(): Array<{
  value: ManualPaymentMethodEnum
  label: string
}> {
  return [
    { value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CHECK, label: 'Check' },
    { value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_WIRE_TRANSFER, label: 'Wire Transfer' },
    { value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_ACH, label: 'ACH' },
    { value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CASH, label: 'Cash' },
  ]
}

/**
 * Hook to export payments as CSV
 *
 * @param invoiceId - The ID of the invoice to export payments for
 * @returns Export function and state
 */
export function useExportInvoicePayments(invoiceId: string) {
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const transport = useTransport()

  const exportPayments = useCallback(async (filters?: PaymentFilterOptions) => {
    setIsExporting(true)
    setError(null)

    try {
      // Create the request with filters
      const request = create(ExportInvoicePaymentsRequestSchema, {
        invoiceId,
        dateFrom: filters?.dateFrom ? timestampFromDate(filters.dateFrom) : undefined,
        dateTo: filters?.dateTo ? timestampFromDate(filters.dateTo) : undefined,
        amountMinCents: filters?.amountMinCents ? BigInt(filters.amountMinCents) : undefined,
        amountMaxCents: filters?.amountMaxCents ? BigInt(filters.amountMaxCents) : undefined,
        stripeOnly: filters?.stripeOnly,
        manualOnly: filters?.manualOnly,
        manualMethods: filters?.manualMethods,
        referenceSearch: filters?.referenceSearch,
        includeVoided: filters?.includeVoided,
      })

      // Make the RPC call using createClient
      const { createClient } = await import('@connectrpc/connect')
      const { CortadoService } = await import('@/gen/cortado/v1/cortado_pb')
      const client = createClient(CortadoService, transport)
      const response = await client.exportInvoicePayments(request)

      // Create blob from CSV data and trigger download
      // Convert Uint8Array to ArrayBuffer for Blob
      const blob = new Blob([new Uint8Array(response.csvData).buffer], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', response.filename || 'payments.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return { success: true, rowCount: response.rowCount }
    } catch (err) {
      const exportError = err instanceof Error ? err : new Error('Export failed')
      setError(exportError)
      throw exportError
    } finally {
      setIsExporting(false)
    }
  }, [invoiceId, transport])

  return {
    exportPayments,
    isExporting,
    error,
  }
}
